import { ReactElement } from 'react'
import { render } from 'react-dom'
import { Subscription } from 'rxjs'
import { announceOptionsMenuClose, optionsMenuClosed$ } from '../../../../NonReact/common/RxJS/RowMenuState'
import { EventNames } from '../../../../NonReact/constants/EventNames'
import { EDGE_BUFFER } from '../../../../NonReact/constants/Misc'
import { PlaceInAkixiNameSpace } from '../../../../NonReact/utils/Utils'
import { BaseEvent } from '../../Types/Types'

export class OptionsMenuAdapter {
    private static OPTIONS_MENU_CLASS: string = 'options-menu'
    private static OPTIONS_MENU_LIST_CLASS: string = 'om-list'

    private menuContainer: HTMLDivElement
    private container: HTMLElement
    private menuList: HTMLUListElement
    private event: MouseEvent | BaseEvent
    private menuClosedSubscription: Definable<Subscription>

    private constructor(event: MouseEvent | BaseEvent, children: ReactElement, container: Nullable<HTMLElement>) {
        this.event = event
        this.container = container ?? document.body
        const { menuContainer, menuList } = this.createAndPositionMenu()

        this.menuContainer = menuContainer
        this.menuList = menuList

        // Remove any existing menus that are open.
        announceOptionsMenuClose()
        this.addMenuDismissal()
        render(children, this.menuList)
        this.postRender()
        this.addClickOutsideDismissal()
    }

    /**
     * Add the document mousedown listener to dismiss the menu when clicking outside.
     */
    private addClickOutsideDismissal(): void {
        const closeMenuEvent = (e: Event): void => {
            if (!e.cancelBubble) {
                announceOptionsMenuClose()
                document.removeEventListener(EventNames.MOUSEDOWN, closeMenuEvent)
            }
        }

        document.addEventListener(EventNames.MOUSEDOWN, closeMenuEvent)
    }

    /**
     * Remove the menu container from the DOM.
     */
    private removeMenu(): void {
        this.menuContainer.remove()
    }

    /**
     * Add the menu dismissal listener.
     */
    private addMenuDismissal(): void {
        if (!this.menuClosedSubscription) {
            this.menuClosedSubscription = optionsMenuClosed$.subscribe((): void => {
                this.menuClosedSubscription?.unsubscribe()
                this.menuClosedSubscription = undefined
                this.removeMenu()
            })
        }
    }

    /**
     * Create the menu and position in the correct place.
     *
     * @param event - The current event from the mouse down action.
     */
    private createAndPositionMenu(): {
        menuContainer: HTMLDivElement
        menuList: HTMLUListElement
    } {
        // Create the required elements.
        const menuContainer: HTMLDivElement = document.createElement('div')
        const menuList: HTMLUListElement = document.createElement('ul')

        menuContainer.classList.add(OptionsMenuAdapter.OPTIONS_MENU_CLASS)
        menuList.classList.add(OptionsMenuAdapter.OPTIONS_MENU_LIST_CLASS)

        menuContainer.style.top = `${this.event.clientY}px`
        menuContainer.style.left = `${this.event.clientX}px`

        // A third of the height of the window.
        const oneThirdHeight: number = window.innerHeight / 3

        // If the event was in the top thrid of the window.
        if (this.event.clientY <= oneThirdHeight) {
            menuContainer.classList.add('menu-position-1-3')
        }

        // If the event was in the second third of the window.
        if (this.event.clientY > oneThirdHeight) {
            menuContainer.classList.add('menu-position-2-3')
        }

        // If the event is in the bottom third of the window.
        if (this.event.clientY > oneThirdHeight * 2) {
            menuContainer.classList.add('menu-position-3-3')
        }

        // Are you in the bottom half of the screen.
        if (this.event.clientY > window.innerHeight / 2) {
            menuContainer.classList.add('menu-upwards')
        }

        // Should the menu go off to the left.
        if (this.event.clientX > window.innerWidth - 200) {
            menuContainer.classList.add('menu-left')
        }

        // Is the event in the right side of the screen.
        if (this.event.clientX > window.innerWidth - 600) {
            menuContainer.classList.add('submenu-left')
        }

        // Appends the menu elements to the DOM.
        menuContainer.appendChild(menuList)
        this.container.appendChild(menuContainer)

        return {
            menuContainer,
            menuList
        }
    }

    /**
     * This is executed after the render method, it does some repositioning after the menu is rendered,
     * given the new positioning.
     */
    private postRender(): void {
        const windowTopLeft: number = 0
        const windowBottom: number = window.innerHeight

        const clientRect: DOMRect = this.menuContainer.getBoundingClientRect()

        if (clientRect.bottom > windowBottom) {
            this.menuContainer.style.top = `${this.event.clientY - (clientRect.bottom - windowBottom + EDGE_BUFFER)}px`
        } else if (clientRect.top < windowTopLeft) {
            this.menuContainer.style.top = `${this.event.clientY + Math.abs(clientRect.top) + EDGE_BUFFER}px`
        }

        if (clientRect.left < windowTopLeft) {
            this.menuContainer.style.left = `${this.event.clientX + Math.abs(clientRect.left) + EDGE_BUFFER}px`
        }
    }

    /**
     * Create a new instance of this class.
     *
     * @param event - The event that generated this menu.
     * @param children - The menu items.
     * @param container - The containing html element for the menu.
     */
    public static newInstance(
        event: MouseEvent | BaseEvent,
        children: ReactElement,
        container: Nullable<HTMLElement>
    ): OptionsMenuAdapter {
        return new OptionsMenuAdapter(event, children, container)
    }
}

PlaceInAkixiNameSpace('OptionsMenuAdapter', OptionsMenuAdapter)
